<template>
  <div class="dashboard-container">
    <!-- 顶部导航栏 -->
    <div class="header-bar">
      <div class="header-left">
        <div class="greeting">
          <span class="greeting-text">下午好，{{ userInfo.staffName || '张院长' }}</span>
          <div class="status-tags">
            <el-tag size="small" type="success" effect="plain" class="status-tag">
              <i class="el-icon-circle-check"></i> 营业中
            </el-tag>
            <el-tag size="small" type="warning" effect="plain" class="status-tag">
              <i class="el-icon-user"></i> {{ currentRole.roleName || '管理员' }}
            </el-tag>
          </div>
        </div>
        <div class="sub-info">
          <span>{{ currentDate }} {{ currentWeekday }}</span>
          <span class="divider">|</span>
          <span class="perm-text">
            共：
            <el-badge :value="permissionStats.dirs" :max="99" class="perm-badge">
              <el-tag type="success" size="small">目录</el-tag>
            </el-badge>
            <el-badge :value="permissionStats.menus" :max="99" class="perm-badge">
              <el-tag type="warning" size="small">菜单</el-tag>
            </el-badge>
            <el-badge :value="permissionStats.btns" :max="99" class="perm-badge">
              <el-tag type="primary" size="small">按钮</el-tag>
            </el-badge>
            <i>{{ permissionStats.total }}</i> 项权限。
          </span>
        </div>
      </div>
      <div class="header-right">
        <!-- 角色切换下拉 -->
        <el-dropdown @command="handleRoleChange" trigger="click" class="role-dropdown">
          <span class="el-dropdown-link account-switch">
            {{ userInfo.staffName || '' }} - {{ currentRole.roleName }}
            <i class="el-icon-arrow-down el-icon--right"></i>
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item 
              v-for="(role, index) in userRoles" 
              :key="index" 
              :command="index"
              :class="{ 'is-active': currentRoleIndex === index }"
            >
              {{ userInfo.staffName || '' }}（{{ role.roleName }}）
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        
        <!-- 帮助与刷新图标 -->
        <el-tooltip content="设计说明" placement="bottom">
          <el-button size="small" icon="el-icon-question" circle class="circle-btn"></el-button>
        </el-tooltip>
        <el-button size="small" icon="el-icon-refresh" class="action-btn" @click="refreshData">刷新</el-button>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="main-content">
      <!-- 模块1：待办事项 -->
      <div class="module-card">
        <div class="module-header">
          <div class="title-wrap">
            <span class="module-num blue">1</span>
            <span class="module-title">待办事项</span>
            <span class="module-subtitle">{{ currentRole.roleName }}角色 · 显示全部业务</span>
          </div>
          <div class="header-extra">
            待处理 <span class="text-blue font-bold">{{ totalPending }}</span> 项 | 今日已办 
            <span class="text-blue font-bold">
              {{ todoData.handledCount || 0 }} / {{ todoData.totalCount || 0 }}
            </span>
          </div>
        </div>
        <div class="todo-grid">
          <!-- 卡片1：待接诊 -->
          <div class="todo-item" @click="goToPage('/reservation/outpatientPhysician')">
            <div class="todo-top">
              <div class="todo-num"><span class="num">{{ todoData.pendingReceptionCount || 0 }}</span> 人</div>
              <div class="todo-icon-bg blue"><i class="el-icon-user"></i></div>
            </div>
            <div class="todo-label">待接诊 <a href="javascript:void(0)" class="text-blue text-sm">我 {{ todoData.myReceptionCount || 0 }}</a></div>
            <div class="todo-progress">
              <div class="progress-text"><span>今日挂号 {{ todoData.totalRegisterCount || 0 }}</span><span>已接诊 {{ todoData.receivedCount || 0 }}</span></div>
              <div class="progress-bar"><div class="progress-inner blue" :style="{ width: getProgress(todoData.receivedCount, todoData.totalRegisterCount) }"></div></div>
            </div>
          </div>
          <!-- 卡片2：待处置 -->
          <div class="todo-item" @click="goToPage('/reservation/outpatientNurse')">
            <div class="todo-top">
              <div class="todo-num"><span class="num">{{ todoData.pendingExecutionCount || 0 }}</span> 项</div>
              <div class="todo-icon-bg purple"><i class="el-icon-edit-outline"></i></div>
            </div>
            <div class="todo-label">待处置</div>
            <div class="todo-progress">
              <div class="progress-text"><span>今日开单 {{ todoData.totalExecutionCount || 0 }}</span><span>已执行 {{ todoData.executedCount || 0 }}</span></div>
              <div class="progress-bar"><div class="progress-inner purple" :style="{ width: getProgress(todoData.executedCount, todoData.totalExecutionCount) }"></div></div>
            </div>
          </div>
          <!-- 卡片3：待发药 -->
          <div class="todo-item" @click="goToPage('/medicenHouse/WesternHouse/newgiveMedici')">
            <div class="todo-top">
              <div class="todo-num"><span class="num">{{ todoData.pendingDispenseOrderCount || 0 }}</span> 单</div>
              <div class="todo-icon-bg green"><i class="el-icon-shopping-cart-full"></i></div>
            </div>
            <div class="todo-label">待发药</div>
            <div class="todo-progress">
              <div class="progress-text"><span>今日处方 {{ todoData.totalDispenseCount || 0 }}</span><span>已发药 {{ todoData.dispensedCount || 0 }}</span></div>
              <div class="progress-bar"><div class="progress-inner green" :style="{ width: getProgress(todoData.dispensedCount, todoData.totalDispenseCount) }"></div></div>
            </div>
          </div>
          <!-- 卡片4：待随访 -->
          <div class="todo-item" @click="goToPage('/FollowUpClinic/FollowUpClinic')">
            <div class="todo-top">
              <div class="todo-num"><span class="num">{{ todoData.pendingFollowCount || 0 }}</span> 人</div>
              <div class="todo-icon-bg orange"><i class="el-icon-phone"></i></div>
            </div>
            <div class="todo-label">待随访</div>
            <div class="todo-progress">
              <div class="progress-text"><span>今日计划 {{ todoData.totalFollowCount || 0 }}</span><span>已完成 {{ todoData.followedCount || 0 }}</span></div>
              <div class="progress-bar"><div class="progress-inner orange" :style="{ width: getProgress(todoData.followedCount, todoData.totalFollowCount) }"></div></div>
            </div>
          </div>
          <!-- 卡片5：待收费 -->
          <div class="todo-item" @click="goToPage('/reservation/outpatientCharge')">
            <div class="todo-top">
              <div class="todo-num"><span class="num">{{ todoData.pendingChargeOrderCount || 0 }}</span> 单</div>
              <div class="todo-icon-bg red"><i class="el-icon-wallet"></i></div>
            </div>
            <div class="todo-label">待收费</div>
            <div class="todo-progress">
              <div class="progress-text"><span>今日开单 {{ todoData.totalChargeCount || 0 }}</span><span>已收费 {{ todoData.chargedCount || 0 }}</span></div>
              <div class="progress-bar"><div class="progress-inner red" :style="{ width: getProgress(todoData.chargedCount, todoData.totalChargeCount) }"></div></div>
            </div>
          </div>
        </div>
        <div class="module-footer-tip">
          待办数量为 0 则显示。卡片下方是今日进度：左边今日总量，右边已完成，进度条为已完成占比。待接诊是唯一有医生归属的待办，非管理员取本人和当前科室，待处置、待发药、待随访、待收费取全院。
        </div>
      </div>

      <!-- 模块2：警示数据 -->
      <div class="module-card">
        <div class="module-header">
          <div class="title-wrap">
            <span class="module-num orange">2</span>
            <span class="module-title">警示数据</span>
            <span class="module-subtitle"><i class="el-icon-warning-outline"></i> 需要处理，不等同于待办</span>
          </div>
          <div class="header-extra text-orange">
            共 <span class="font-bold">0</span> 项异常
          </div>
        </div>
        
        <!-- 中间区域已按要求置空 -->
        <div class="empty-warning-space"></div>

        <div class="module-footer-tip" style="margin-top: 0;">
          数量为 0 的预警不显示；全部为 0 时本块隐藏。库存下限、临期天数、挂账超期天数从系统参数读取。
        </div>
      </div>

      <!-- 模块3：经营数据（仅管理员/老板可见） -->
      <div class="module-card" v-if="isAdmin">
        <div class="module-header">
          <div class="title-wrap">
            <span class="module-num purple">3</span>
            <span class="module-title">经营数据</span>
            <el-tag size="mini" class="admin-only-tag">仅管理员角色可见</el-tag>
            <span class="module-subtitle">全院收支与成本分析</span>
          </div>
          <div class="header-extra">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              size="small"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              class="date-picker"
              @change="handleDateChange"
            >
            </el-date-picker>
          </div>
        </div>
        
        <!-- 经营概览数据 -->
        <div class="finance-overview">
          <div class="finance-card">
            <div class="finance-label">本期收入</div>
            <div class="finance-value">¥{{ formatMoneyWan(financeData.allIncomeAmount) }}</div>
            <div class="finance-desc">就诊收费 + 挂号收费</div>
          </div>
          <div class="finance-card">
            <div class="finance-label">本期成本</div>
            <div class="finance-value text-orange">¥{{ formatMoneyWan(financeData.allCostAmount) }}</div>
            <div class="finance-desc">药品 + 卫材 + 诊疗 + 费用</div>
          </div>
          <div class="finance-card">
            <div class="finance-label">本期利润</div>
            <div class="finance-value text-green">¥{{ formatMoneyWan(financeData.allProfitAmount) }}</div>
            <div class="finance-desc">收入减成本</div>
          </div>
          <div class="finance-card">
            <div class="finance-label">利润率</div>
            <div class="finance-value text-green">{{ financeData.profitRatio || '0.00%' }}</div>
            <div class="finance-desc">利润 ÷ 收入</div>
          </div>
          <div class="finance-card">
            <div class="finance-label">成本率</div>
            <div class="finance-value text-orange">{{ financeData.costRatio || '0.00%' }}</div>
            <div class="finance-desc">成本 ÷ 收入</div>
          </div>
        </div>

        <!-- 图表区域1：饼图 -->
        <div class="charts-row">
          <div class="chart-card">
            <div class="chart-header">
              <span class="chart-title">收入构成</span>
              <span class="chart-subtitle">按收入分类</span>
            </div>
            <div ref="incomeChart" class="chart-container"></div>
          </div>
          <div class="chart-card">
            <div class="chart-header">
              <span class="chart-title">成本构成</span>
              <span class="chart-subtitle">按成本分类</span>
            </div>
            <div ref="costChart" class="chart-container"></div>
          </div>
        </div>

        <!-- 图表区域2：折线图 -->
        <div class="chart-card full-width">
          <div class="chart-header">
            <span class="chart-title">收支趋势 <span class="text-gray text-sm">{{ trendDesc }}</span></span>
            <div class="legend">
              <span class="legend-item"><span class="dot blue"></span>收入</span>
              <span class="legend-item"><span class="dot orange"></span>成本</span>
            </div>
          </div>
          <div ref="trendChart" class="chart-container-trend"></div>
        </div>

        <!-- 图表区域3：横向柱状图与列表 -->
        <div class="charts-row">
          <!-- 药品销售与毛利 -->
          <div class="chart-card">
            <div class="chart-header">
              <span class="chart-title">药品销售与毛利</span>
              <span class="chart-subtitle">按销售额降序取前 10</span>
            </div>
            <div class="custom-bar-list overflow-x-auto">
              <div class="list-header">
                <span class="col-rank">排名</span>
                <span class="col-name">名称</span>
                <span class="col-bar"></span>
                <span class="col-value">销售额</span>
                <span class="col-qty">销售量</span>
              </div>
              <div class="list-item" v-for="(item, index) in drugSales" :key="index">
                <div class="item-rank" :class="{'top3': index < 3}">{{ index + 1 }}</div>
                <div class="item-name" :title="item.name">{{ item.name }}</div>
                <div class="item-bar-wrap">
                  <div class="item-bar green" :style="{ width: getBarWidth(item.sales, maxDrugSales) }"></div>
                </div>
                <div class="item-value">¥{{ item.sales.toLocaleString() }}</div>
                <div class="item-qty">{{ item.qty }}</div>
              </div>
              <div v-if="drugSales.length === 0" class="empty-tip">暂无数据</div>
            </div>
          </div>
          
          <!-- 处方毛利 -->
          <div class="chart-card flex-layout-card">
            <div class="chart-header">
              <span class="chart-title">处方毛利</span>
              <span class="chart-subtitle">按处方类型：销售额 - 成本 = 毛利</span>
            </div>
            <div class="prescription-margin-list overflow-x-auto">
              <div class="list-header">
                <span class="col-type">处方类型</span>
                <span class="col-val">销售额</span>
                <span class="col-val">成本</span>
                <span class="col-val">毛利</span>
                <span class="col-val">毛利率</span>
                <span class="col-val">处方数</span>
              </div>
              <div class="list-item" v-for="(item, index) in prescriptionMarginList" :key="index">
                <div class="col-type">
                  <span class="dot" :class="item.color"></span>{{ item.name }}
                </div>
                <div class="col-val">{{ item.sales }}</div>
                <div class="col-val text-orange">{{ item.cost }}</div>
                <div class="col-val text-green">{{ item.margin }}</div>
                <div class="col-val">{{ item.rate }}</div>
                <div class="col-val">{{ item.count }}</div>
              </div>
              <div class="list-item total-row">
                <div class="col-type">合计</div>
                <div class="col-val font-bold">{{ prescriptionMarginTotal.sales }}</div>
                <div class="col-val font-bold text-orange">{{ prescriptionMarginTotal.cost }}</div>
                <div class="col-val font-bold text-green">{{ prescriptionMarginTotal.margin }}</div>
                <div class="col-val font-bold">{{ prescriptionMarginTotal.rate }}</div>
                <div class="col-val font-bold">{{ prescriptionMarginTotal.count }}</div>
              </div>
              <div v-if="prescriptionMarginList.length === 0" class="empty-tip">暂无数据</div>
            </div>
            <!-- 说明文字放在最底部 -->
            <div class="module-footer-tip" style="margin-top: auto;">
              <i class="el-icon-info"></i> 按开单时的处方类型汇总本期数据：西药处方、中药处方取药品毛利表，治疗项目取诊疗项目毛利表；处方数只统计西药、中药两类，治疗项目按治疗开单计不数列数。
            </div>
          </div>
        </div>

        <!-- 图表区域4：医生贡献与科室贡献 -->
        <div class="charts-row">
          <!-- 医生贡献度 -->
          <div class="chart-card">
            <div class="chart-header">
              <span class="chart-title">医生贡献度</span>
              <el-radio-group v-model="doctorRadio" size="mini" class="chart-radio">
                <el-radio-button label="创收"></el-radio-button>
                <el-radio-button label="接诊量"></el-radio-button>
              </el-radio-group>
            </div>
            <div class="custom-bar-list doctor-list">
              <div class="list-item" v-for="(item, index) in doctorList" :key="index">
                <div class="item-rank" :class="{'top3': index < 3}">{{ index + 1 }}</div>
                <div class="item-name" :title="item.name">{{ item.name }}</div>
                <div class="item-bar-wrap">
                  <div class="item-bar blue" :style="{ width: getBarWidth(item.amount, maxDoctorIncome) }"></div>
                </div>
                <div class="item-value">¥{{ item.amount.toLocaleString() }} <span class="text-gray">{{ item.percent }}</span></div>
              </div>
              <div v-if="doctorList.length === 0" class="empty-tip">暂无数据</div>
            </div>
            <div class="doctor-footer">
              <div>合计创收<br><span class="font-bold text-lg">¥{{ formatMoney(doctorTotalIncome) }}</span></div>
              <div>人均创收<br><span class="font-bold text-lg">¥{{ formatMoney(doctorAvgIncome) }}</span></div>
              <div>平均药占比<br><span class="font-bold text-lg">60.7%</span></div>
            </div>
          </div>

          <!-- 科室贡献 -->
          <div class="chart-card">
            <div class="chart-header">
              <span class="chart-title">科室贡献</span>
              <span class="chart-subtitle">按科室汇总收入</span>
            </div>
            <div ref="departmentChart" class="chart-container-bar"></div>
            <div class="module-footer-tip" style="margin-top: 10px;">
              <i class="el-icon-info"></i> 诊所只有一个科室时，这组图不显示
            </div>
          </div>
        </div>
        
        <!-- 底部说明 -->
        <div class="finance-footer-tip">
          <p><i class="el-icon-info"></i> 本层只放金额、成本与毛利。挂号人次、就诊人次、处方张数属于业务量，在第一层待办卡片的今日进度里。</p>
          <p>• 收入、成本、利润三者始终自洽：利润 = 收入 - 成本，利润率 = 成本率 = 100%。</p>
          <p>• 成本四项互不重复：药品成本按进价计，卫材成本按出库进价计，诊疗成本按治疗项目成本计，费用支出只取一般费用单里的经营性支出。</p>
          <p>• 趋势图横轴按区间天数固定分档：不足两天画 2~30 天每天一个点，31~90 天每 7 天一个点，91 天到 3 年每 30 天一个点，超过 3 年每 365 天一个点。</p>
        </div>
      </div>
    </div>

    <!-- 回到顶部按钮 -->
    <el-backtop :bottom="100" :right="100" class="custom-backtop">
      <div class="back-top-inner">
        <i class="el-icon-caret-top"></i>
      </div>
    </el-backtop>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { statistV2, manageReport } from "@/api/index.js";

export default {
  name: 'Dashboard',
  data() {
    return {
      userInfo: {},
      userRoles: [],
      currentRoleIndex: 0,
      currentRole: {},
      currentDate: '',
      currentWeekday: '',
      dateRange: [],
      doctorRadio: '创收',
      permissionStats: {
        dirs: 0,
        menus: 0,
        btns: 0,
        total: 0
      },
      
      todoData: {
        pendingReceptionCount: 0,
        pendingExecutionCount: 0,
        pendingChargeOrderCount: 0,
        pendingDispenseOrderCount: 0,
        pendingFollowCount: 0,
        handledCount: 0,
        totalCount: 0,
        myReceptionCount: 0,
        totalRegisterCount: 0,
        receivedCount: 0,
        totalExecutionCount: 0,
        executedCount: 0,
        totalDispenseCount: 0,
        dispensedCount: 0,
        totalFollowCount: 0,
        followedCount: 0,
        totalChargeCount: 0,
        chargedCount: 0
      },

      financeData: {},
      drugSales: [],
      prescriptionMarginList: [],
      prescriptionMarginTotal: {
        sales: '¥0.00', cost: '¥0.00', margin: '¥0.00', rate: '0.00%', count: '0 张'
      },
      doctorList: [],
      doctorTotalIncome: 0,
      doctorAvgIncome: 0,
      
      maxDrugSales: 1,
      maxDoctorIncome: 1,
      
      trendDesc: '共 0 天',
      chartInstances: {}
    };
  },
  computed: {
    isAdmin() {
      const roleName = this.currentRole.roleName || '';
      return roleName.includes('管理员') || roleName.includes('老板');
    },
    totalPending() {
      return (this.todoData.pendingReceptionCount || 0) + 
             (this.todoData.pendingExecutionCount || 0) + 
             (this.todoData.pendingChargeOrderCount || 0) + 
             (this.todoData.pendingDispenseOrderCount || 0) + 
             (this.todoData.pendingFollowCount || 0);
    }
  },
  mounted() {
    this.initUserInfo();
    this.initDate();
    this.initDefaultDateRange();
    this.calculatePermissions();
    this.getTodoStatist();
    
    this.$nextTick(() => {
      if (this.isAdmin) {
        this.getFinanceData();
      }
    });
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    Object.values(this.chartInstances).forEach(chart => chart && chart.dispose());
  },
  methods: {
    initDefaultDateRange() {
      const end = new Date();
      const start = new Date();
      start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
      
      const formatDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      };
      this.dateRange = [formatDate(start), formatDate(end)];
    },
    handleDateChange() {
      if (this.isAdmin) {
        this.getFinanceData();
      }
    },
    formatDateTime(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      const s = String(date.getSeconds()).padStart(2, '0');
      return `${y}-${m}-${d} ${h}:${min}:${s}`;
    },
    getProgress(completed, total) {
      if (!total || total === 0) return '0%';
      const percent = (completed / total) * 100;
      return Math.min(100, percent) + '%';
    },
    async getTodoStatist() {
      try {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        
        const params = {
          startTime: this.formatDateTime(start),
          endTime: this.formatDateTime(end)
        };
        
        const res = await statistV2(params);
        
        if (res && (res.code === 200 || res.code === '200')) {
          const data = res.data || res;
          this.todoData = {
            pendingReceptionCount: data.pendingReceptionCount || 0,
            pendingExecutionCount: data.pendingExecutionCount || 0,
            pendingChargeOrderCount: data.pendingChargeOrderCount || 0,
            pendingDispenseOrderCount: data.pendingDispenseOrderCount || 0,
            pendingFollowCount: data.pendingFollowCount || 0,
            handledCount: data.handledCount || 0,
            totalCount: data.totalCount || 0,
            myReceptionCount: data.myReceptionCount || 0,
            totalRegisterCount: data.totalRegisterCount || 0,
            receivedCount: data.receivedCount || 0,
            totalExecutionCount: data.totalExecutionCount || 0,
            executedCount: data.executedCount || 0,
            totalDispenseCount: data.totalDispenseCount || 0,
            dispensedCount: data.dispensedCount || 0,
            totalFollowCount: data.totalFollowCount || 0,
            followedCount: data.followedCount || 0,
            totalChargeCount: data.totalChargeCount || 0,
            chargedCount: data.chargedCount || 0
          };
        }
      } catch (error) {
        console.error('获取待办统计数据失败:', error);
      }
    },
    async getFinanceData() {
      if (!this.dateRange || this.dateRange.length !== 2) return;
      
      try {
        const params = {
          startDate: `${this.dateRange[0]} 00:00:00`,
          endDate: `${this.dateRange[1]} 23:59:59`
        };
        
        const res = await manageReport(params);
        
        if (res && (res.code === 200 || res.code === '200')) {
          this.financeData = res.data || {};
          this.processFinanceData();
          
          this.$nextTick(() => {
            this.initCharts();
          });
        }
      } catch (error) {
        console.error('获取经营数据失败:', error);
      }
    },
    processFinanceData() {
      const data = this.financeData;
      
      const drugs = data.drugVoList || [];
      this.drugSales = drugs.slice(0, 10).map(d => ({
        name: d.drugName,
        sales: d.saleAmount,
        qty: d.saleNum
      }));
      this.maxDrugSales = Math.max(1, ...this.drugSales.map(d => d.sales));

      const orders = data.orderVoList || [];
      let totalSale = 0, totalCost = 0, totalProfit = 0, totalOrderNum = 0;
      const colors = ['blue', 'green', 'orange', 'red', 'purple'];
      
      this.prescriptionMarginList = orders.map((o, index) => {
        totalSale += (o.saleAmount || 0);
        totalCost += (o.costAmount || 0);
        totalProfit += (o.profitAmount || 0);
        totalOrderNum += (o.orderNum || 0);
        
        return {
          name: o.orderTypeName,
          color: colors[index % colors.length],
          sales: `¥${(o.saleAmount || 0).toLocaleString()}`,
          cost: `¥${(o.costAmount || 0).toLocaleString()}`,
          margin: `¥${(o.profitAmount || 0).toLocaleString()}`,
          rate: o.profitRatio || '0.00%',
          count: o.orderNum ? `${o.orderNum} 张` : '—'
        };
      });
      
      const totalRate = totalSale > 0 ? ((totalProfit / totalSale) * 100).toFixed(1) + '%' : '0.00%';
      this.prescriptionMarginTotal = {
        sales: `¥${totalSale.toLocaleString()}`,
        cost: `¥${totalCost.toLocaleString()}`,
        margin: `¥${totalProfit.toLocaleString()}`,
        rate: totalRate,
        count: totalOrderNum > 0 ? `${totalOrderNum} 张` : '—'
      };

      const doctors = data.doctorVoList || [];
      let dTotal = 0;
      doctors.forEach(d => { dTotal += (d.incomeAmount || 0); });
      this.doctorTotalIncome = dTotal;
      
      this.doctorList = doctors.slice(0, 10).map(d => {
        const income = d.incomeAmount || 0;
        const percent = dTotal > 0 ? ((income / dTotal) * 100).toFixed(1) + '%' : '0.0%';
        return {
          name: d.doctorName,
          amount: income,
          percent: percent
        };
      });
      this.maxDoctorIncome = Math.max(1, ...this.doctorList.map(d => d.amount));
      this.doctorAvgIncome = this.doctorList.length > 0 ? Math.round(dTotal / this.doctorList.length) : 0;

      const trendList = data.trendVoList || [];
      this.trendDesc = `共 ${trendList.length} 天`;
    },
    formatMoneyWan(val) {
      if (!val && val !== 0) return '0.00万';
      return (val / 10000).toFixed(2) + '万';
    },
    formatMoney(val) {
      if (!val && val !== 0) return '0';
      return val.toLocaleString();
    },
    getBarWidth(val, max) {
      if (!max || max === 0) return '0%';
      return Math.min(100, (val / max) * 100) + '%';
    },
    refreshData() {
      this.getTodoStatist();
      if (this.isAdmin) {
        this.getFinanceData();
      }
      this.$message.success('数据已刷新');
    },
    initUserInfo() {
      const savedInfo = JSON.parse(localStorage.getItem('userInfo')) || {};
      this.userInfo = {
        staffName: savedInfo.staffName || '张院长',
        ...savedInfo
      };
      
      let savedRoles = [];
      try {
        savedRoles = JSON.parse(localStorage.getItem('userRoles') || '[]');
      } catch (e) {
        console.warn('解析 userRoles 失败', e);
      }
      
      if (savedRoles && savedRoles.length > 0) {
        this.userRoles = savedRoles;
      } else {
        this.userRoles = [
          { roleName: '管理员', roleId: '1' },
          { roleName: '医生', roleId: '2' },
          { roleName: '药师', roleId: '3' },
          { roleName: '收费员', roleId: '4' }
        ];
      }
      
      this.currentRole = this.userRoles[0];
      this.currentRoleIndex = 0;
    },
    initDate() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      this.currentDate = `${year}年${month}月${day}日`;
      
      const weekdays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
      this.currentWeekday = weekdays[now.getDay()];
    },
    calculatePermissions() {
      let menus = [];
      try {
        const savedMenus = sessionStorage.getItem('Menus');
        if (savedMenus) {
          menus = JSON.parse(savedMenus);
        }
      } catch (e) {
        console.warn('解析 Menus 失败', e);
      }
      
      let dirs = 0;
      let m = 0;
      let b = 0;
      
      const traverse = (list) => {
        if (!list || !Array.isArray(list)) return;
        list.forEach(item => {
          if (item.type === 'C') dirs++;
          else if (item.type === 'M') m++;
          else if (item.type === 'F') b++;
          
          if (item.children && item.children.length > 0) {
            traverse(item.children);
          }
        });
      };
      
      traverse(menus);
      
      this.permissionStats = {
        dirs,
        menus: m,
        btns: b,
        total: dirs + m + b
      };
    },
    handleRoleChange(index) {
      this.currentRoleIndex = index;
      this.currentRole = this.userRoles[index];
      
      this.getTodoStatist();
      
      this.$nextTick(() => {
        if (this.isAdmin) {
          this.getFinanceData();
        } else {
          Object.values(this.chartInstances).forEach(chart => chart && chart.dispose());
          this.chartInstances = {};
        }
      });
    },
    goToPage(path) {
      if (this.$router) {
        this.$router.push(path).catch(err => {
          console.error('路由跳转失败:', err);
        });
      } else {
        console.log('模拟跳转至:', path);
      }
    },
    handleResize() {
      Object.values(this.chartInstances).forEach(chart => chart && chart.resize());
    },
    initCharts() {
      Object.values(this.chartInstances).forEach(chart => chart && chart.dispose());
      
      const data = this.financeData;
      if (!data || Object.keys(data).length === 0) return;

      const incomeVo = data.incomeVo || {};
      const incomeData = [
        { value: incomeVo.registrationIncome || 0, name: '挂号' },
        { value: incomeVo.drugIncome || 0, name: '西药' },
        { value: incomeVo.tcmDrugIncome || 0, name: '中药' },
        { value: incomeVo.treatIncome || 0, name: '治疗' },
        { value: (incomeVo.eisaiIncome || 0) + (incomeVo.checkIncome || 0) + (incomeVo.otherMedicineIncome || 0) + (incomeVo.otherIncome || 0), name: '其他' }
      ].filter(item => item.value > 0);

      const incomeChartDom = this.$refs.incomeChart;
      if (incomeChartDom) {
        const chart = echarts.init(incomeChartDom);
        chart.setOption({
          tooltip: { trigger: 'item' },
          legend: { orient: 'vertical', right: '10%', top: 'center' },
          color: ['#1677ff', '#00b578', '#ff8f1f', '#ff4d4f', '#999'],
          series: [
            {
              name: '收入构成',
              type: 'pie',
              radius: ['50%', '70%'],
              avoidLabelOverlap: false,
              label: { 
                show: true, 
                position: 'center', 
                formatter: `本期收入\n¥${this.formatMoneyWan(incomeVo.allIncomeAmount || 0)}`, 
                fontSize: 14, 
                fontWeight: 'bold' 
              },
              labelLine: { show: false },
              data: incomeData.length ? incomeData : [{ value: 1, name: '暂无数据', itemStyle: { color: '#eee' } }]
            }
          ]
        });
        this.chartInstances.income = chart;
      }

      const costVo = data.costVo || {};
      const costData = [
        { value: costVo.drugCostAmount || 0, name: '药品成本' },
        { value: costVo.medicalCostAmount || 0, name: '诊疗成本' },
        { value: costVo.expenseCostAmount || 0, name: '费用支出' },
        { value: costVo.eisaiCostAmount || 0, name: '卫材成本' }
      ].filter(item => item.value > 0);

      const costChartDom = this.$refs.costChart;
      if (costChartDom) {
        const chart = echarts.init(costChartDom);
        chart.setOption({
          tooltip: { trigger: 'item' },
          legend: { orient: 'vertical', right: '10%', top: 'center' },
          color: ['#00b578', '#ff4d4f', '#ff8f1f', '#722ed1'],
          series: [
            {
              name: '成本构成',
              type: 'pie',
              radius: ['50%', '70%'],
              avoidLabelOverlap: false,
              label: { 
                show: true, 
                position: 'center', 
                formatter: `本期成本\n¥${this.formatMoneyWan(data.allCostAmount || 0)}`, 
                fontSize: 14, 
                fontWeight: 'bold' 
              },
              labelLine: { show: false },
              data: costData.length ? costData : [{ value: 1, name: '暂无数据', itemStyle: { color: '#eee' } }]
            }
          ]
        });
        this.chartInstances.cost = chart;
      }

      const trendVoList = data.trendVoList || [];
      const xAxisData = trendVoList.map(item => item.timeStr);
      const incomeLine = trendVoList.map(item => (item.incomeAmount / 1000).toFixed(1)); 
      const costLine = trendVoList.map(item => (item.costAmount / 1000).toFixed(1));

      const trendChartDom = this.$refs.trendChart;
      if (trendChartDom) {
        const chart = echarts.init(trendChartDom);
        chart.setOption({
          tooltip: { trigger: 'axis' },
          grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisData,
            axisLine: { lineStyle: { color: '#ccc' } },
            axisLabel: { color: '#999' }
          },
          yAxis: {
            type: 'value',
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { lineStyle: { type: 'dashed', color: '#eee' } },
            axisLabel: { color: '#999', formatter: '{value}k' }
          },
          series: [
            {
              name: '收入',
              type: 'line',
              smooth: true,
              symbol: 'none',
              lineStyle: { width: 2, color: '#1677ff' },
              areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{offset: 0, color: 'rgba(22,119,255,0.2)'}, {offset: 1, color: 'rgba(22,119,255,0.01)'}]) },
              data: incomeLine
            },
            {
              name: '成本',
              type: 'line',
              smooth: true,
              symbol: 'none',
              lineStyle: { width: 2, color: '#ff8f1f' },
              data: costLine
            }
          ]
        });
        this.chartInstances.trend = chart;
      }

      const deptVoList = data.deptVoList || [];
      const deptNames = deptVoList.map(item => item.deptName);
      const deptValues = deptVoList.map(item => (item.incomeAmount / 10000).toFixed(1)); 

      const deptChartDom = this.$refs.departmentChart;
      if (deptChartDom) {
        const chart = echarts.init(deptChartDom);
        chart.setOption({
          tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
          grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
          xAxis: {
            type: 'category',
            data: deptNames.length ? deptNames : ['暂无数据'],
            axisLine: { lineStyle: { color: '#ccc' } },
            axisLabel: { color: '#666' }
          },
          yAxis: {
            type: 'value',
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { lineStyle: { type: 'dashed', color: '#eee' } },
            axisLabel: { color: '#999', formatter: '{value}万' }
          },
          series: [
            {
              name: '收入',
              type: 'bar',
              barWidth: '40%',
              itemStyle: { color: '#8b5cf6', borderRadius: [4, 4, 0, 0] },
              data: deptValues.length ? deptValues : [0]
            }
          ]
        });
        this.chartInstances.dept = chart;
      }
    }
  }
};
</script>

<style lang="less" scoped>
.dashboard-container {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 40px; 
}

::v-deep .el-badge__content{
	font-size: 8px;
	height: 14px;
	line-height: 14px;
	padding: 0 4px;
}

/* 顶部导航栏 */
.header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  box-shadow: none;
  padding: 16px 24px;
  z-index: 10;
  flex-wrap: wrap; /* 允许小屏幕自动换行 */
  gap: 16px;

  .header-left {
    .greeting {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: 8px;
      flex-wrap: wrap;

      .greeting-text {
        font-size: 24px;
        font-weight: 600;
        color: #1f2937;
      }

      .status-tags {
        display: flex;
        gap: 8px;
        
        .status-tag {
          border-radius: 4px;
          font-size: 12px;
          padding: 0 8px;
          height: 24px;
          line-height: 22px;
        }
      }
    }

    .sub-info {
      font-size: 13px;
      color: #6b7280;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;

      .divider {
        color: #d1d5db;
      }
      
      .perm-text {
        display: inline-flex;
        align-items: center;
        flex-wrap: wrap;
        i {
          font-weight: 500;
          margin: 0 6px;
          color: #aa0000;
          margin-left: 8px;
        }

        .perm-badge {
          display: inline-flex;
          align-items: center;
          margin-left: 6px;
        }
      }
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    .role-dropdown {
      .account-switch {
        display: flex;
        align-items: center;
        cursor: pointer;
        font-size: 14px;
        color: #374151;
        padding: 6px 12px;
        background: #f3f4f6;
        border-radius: 6px;
        border: 1px solid #e5e7eb;
        transition: all 0.2s;
        
        &:hover {
          border-color: #1677ff;
          color: #1677ff;
        }
      }
    }

    .circle-btn {
      border-radius: 50%;
      color: #4b5563;
      border-color: #d1d5db;
      padding: 8px;
      font-size: 16px;
      transition: all 0.2s;
      
      &:hover {
        color: #1677ff;
        border-color: #1677ff;
        background: #f0f7ff;
      }
    }

    .action-btn {
      color: #4b5563;
      border-color: #d1d5db;
      transition: all 0.2s;
      font-size: 13px;
      padding: 8px 16px;
      border-radius: 6px;

      &:hover {
        color: #1677ff;
        border-color: #1677ff;
        background: #f0f7ff;
      }
    }
  }
}

/deep/ .el-dropdown-menu__item.is-active {
  background-color: #e6f7ff;
  color: #1677ff;
  font-weight: 600;
}

.main-content {
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.module-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #ebeef5;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  }

  .module-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    flex-wrap: wrap; /* 小屏换行 */
    gap: 10px;

    .title-wrap {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;

      .module-num {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        font-weight: bold;
        font-size: 14px;
        color: #fff;
        margin-right: 4px;
        
        &.blue { background: #1677ff; }
        &.orange { background: #ff8f1f; }
        &.purple { background: #7c3aed; }
      }

      .module-title {
        font-size: 16px;
        font-weight: 600;
        color: #1f2937;
      }

      .module-subtitle {
        font-size: 12px;
        color: #9ca3af;
        margin-left: 8px;
      }
      
      .admin-only-tag {
        background: #f3e8ff;
        color: #7c3aed;
        border-color: #d8b4fe;
      }
    }

    .header-extra {
      font-size: 13px;
      color: #6b7280;
    }
  }

  .module-footer-tip {
    font-size: 12px;
    color: #9ca3af;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed #f0f0f0;
    line-height: 1.5;
  }
  
  .empty-warning-space {
    height: 20px;
  }
}

/* 待办事项网格 */
.todo-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;

  .todo-item {
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    padding: 16px;
    transition: all 0.3s;
    cursor: pointer;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      border-color: #d1d5db;
      transform: translateY(-2px);
    }

    .todo-top {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 12px;

      .todo-num {
        font-size: 14px;
        color: #4b5563;
        .num {
          font-size: 28px;
          font-weight: bold;
          color: #1f2937;
          margin-right: 4px;
        }
      }

      .todo-icon-bg {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        
        &.blue { background: #e6f7ff; color: #1677ff; }
        &.purple { background: #f3e8ff; color: #7c3aed; }
        &.green { background: #e8f5e9; color: #00b578; }
        &.orange { background: #fff7e6; color: #ff8f1f; }
        &.red { background: #ffebee; color: #ff4d4f; }
      }
    }

    .todo-label {
      font-size: 14px;
      font-weight: 500;
      color: #374151;
      margin-bottom: 12px;
    }

    .todo-progress {
      .progress-text {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: #9ca3af;
        margin-bottom: 6px;
      }
      .progress-bar {
        height: 6px;
        background: #f0f0f0;
        border-radius: 3px;
        overflow: hidden;
        
        .progress-inner {
          height: 100%;
          border-radius: 3px;
          &.blue { background: #1677ff; }
          &.purple { background: #7c3aed; }
          &.green { background: #00b578; }
          &.orange { background: #ff8f1f; }
          &.red { background: #ff4d4f; }
        }
      }
    }
  }
}

/* 经营数据 */
.finance-overview {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 20px;

  .finance-card {
    background: #f9fafb;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    padding: 16px;

    .finance-label {
      font-size: 13px;
      color: #6b7280;
      margin-bottom: 8px;
    }
    .finance-value {
      font-size: 24px;
      font-weight: bold;
      color: #1f2937;
      margin-bottom: 8px;
    }
    .finance-desc {
      font-size: 12px;
      color: #9ca3af;
    }
  }
}

/* 图表行容器 */
.charts-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.chart-card {
  min-width: 0; 
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 16px;
  background: #fff;

  &.full-width {
    grid-column: 1 / -1;
    margin-bottom: 20px;
  }
  
  &.flex-layout-card {
    display: flex;
    flex-direction: column;
  }

  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    flex-wrap: wrap; /* 小屏幕标题可能换行 */
    gap: 8px;

    .chart-title {
      font-size: 15px;
      font-weight: 600;
      color: #1f2937;
    }
    .chart-subtitle {
      font-size: 12px;
      color: #9ca3af;
    }
    .legend {
      display: flex;
      gap: 16px;
      font-size: 12px;
      color: #6b7280;
      .legend-item {
        display: flex;
        align-items: center;
        gap: 4px;
        .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          &.blue { background: #1677ff; }
          &.orange { background: #ff8f1f; }
        }
      }
    }
  }

  .chart-container { height: 240px; width: 100%; }
  .chart-container-trend { height: 200px; width: 100%; }
  .chart-container-bar { height: 260px; width: 100%; }
}

/* 列表横向滚动容器 */
.overflow-x-auto {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: #dcdfe6; border-radius: 2px; }
}

/* 自定义横向列表柱状图 (药品销售/医生贡献) */
.custom-bar-list {
  min-width: 300px; /* 确保内容不挤压，小屏内部滚动 */

  .list-header {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #9ca3af;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
    
    .col-rank { width: 34px; flex-shrink: 0; text-align: left; }
    .col-name { width: 80px; flex-shrink: 0; }
    .col-bar { flex: 1; }
    .col-value { width: 80px; text-align: right; flex-shrink: 0; }
    .col-qty { width: 50px; text-align: right; flex-shrink: 0; }
  }

  .list-item {
    display: flex;
    align-items: center;
    padding: 4px 0;
    font-size: 12px;

    .item-rank {
      width: 16px;
      height: 16px;
      line-height: 16px;
      text-align: center;
      border-radius: 50%;
      background: #f3f4f6;
      color: #6b7280;
      font-size: 10px;
      margin-right: 18px;
      flex-shrink: 0;
      
      &.top3 {
        background: #1677ff;
        color: #fff;
      }
    }

    .item-name {
      width: 80px;
      flex-shrink: 0;
      color: #374151;
      font-size: 12px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .item-bar-wrap {
      flex: 1;
      margin: 0 10px;
      height: 4px;
      background: #f3f4f6;
      border-radius: 2px;
      
      .item-bar {
        height: 100%;
        border-radius: 2px;
        &.green { background: #00b578; }
        &.blue { background: #1677ff; }
      }
    }

    .item-value {
      width: 80px;
      text-align: right;
      color: #1f2937;
      font-weight: 500;
      flex-shrink: 0;
      font-size: 12px;
    }
    .item-qty {
      width: 50px;
      text-align: right;
      color: #6b7280;
      flex-shrink: 0;
      font-size: 12px;
    }
  }

  .empty-tip {
    text-align: center;
    color: #9ca3af;
    font-size: 12px;
    padding: 20px 0;
  }
}

.doctor-list {
  .list-item {
    .item-bar-wrap {
      flex: 1;
    }
    .item-value {
      width: 110px;
      flex-shrink: 0;
      font-size: 12px;
    }
  }
}
.doctor-footer {
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding-top: 16px;
  margin-top: auto;
  border-top: 1px solid #f0f0f0;
  text-align: center;
  font-size: 12px;
  color: #6b7280;
}

/* 处方毛利列表 */
.prescription-margin-list {
  min-width: 400px; /* 保证列不被挤压太严重 */

  .list-header {
    display: flex;
    font-size: 12px;
    color: #9ca3af;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
    
    .col-type { width: 80px; flex-shrink: 0; }
    .col-val { flex: 1; text-align: right; }
  }

  .list-item {
    display: flex;
    align-items: center;
    padding: 4px 0;
    font-size: 12px;

    .col-type {
      width: 80px;
      flex-shrink: 0;
      color: #374151;
      display: flex;
      align-items: center;
      gap: 4px;
      
      .dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        &.blue { background: #1677ff; }
        &.green { background: #00b578; }
        &.orange { background: #ff8f1f; }
      }
    }
    .col-val {
      flex: 1;
      text-align: right;
      color: #1f2937;
    }
  }

  .total-row {
    border-bottom: none;
    padding-top: 12px;
    padding-bottom: 12px;
    .col-type { color: #6b7280; }
  }

  .empty-tip {
    text-align: center;
    color: #9ca3af;
    font-size: 12px;
    padding: 20px 0;
  }
}

/* 底部说明 */
.finance-footer-tip {
  margin-top: 10px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.8;
  
  p {
    margin: 0;
  }
}

/* 回到顶部按钮 */
.custom-backtop {
  .back-top-inner {
    width: 40px;
    height: 40px;
    background-color: #fff;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1677ff;
    font-size: 20px;
    transition: all 0.3s;
    
    &:hover {
      background-color: #f0f7ff;
      transform: translateY(-2px);
    }
  }
}

/* 通用颜色辅助类 */
.text-blue { color: #1677ff; }
.text-green { color: #00b578; }
.text-orange { color: #ff8f1f; }
.text-gray { color: #9ca3af; font-size: 12px; }
.font-bold { font-weight: bold; }
.text-lg { font-size: 16px; }

/* =========================================================
   多端响应式适配
   ========================================================= */
@media (max-width: 1400px) {
  .todo-grid { grid-template-columns: repeat(4, 1fr); }
  .finance-overview { grid-template-columns: repeat(4, 1fr); }
}

@media (max-width: 1200px) {
  .todo-grid { grid-template-columns: repeat(3, 1fr); }
  .finance-overview { grid-template-columns: repeat(3, 1fr); }
  .charts-row { grid-template-columns: 1fr; } /* 图表行在中等屏幕改为上下堆叠 */
}

@media (max-width: 992px) {
  .todo-grid { grid-template-columns: repeat(2, 1fr); }
  .finance-overview { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 768px) {
  .header-bar {
    padding: 12px 16px;
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-right {
    width: 100%;
    justify-content: flex-start;
    margin-top: 8px;
  }

  .main-content {
    padding: 0 12px;
    gap: 12px;
  }

  .module-card {
    padding: 16px;
  }

  .todo-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  
  .finance-overview {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .greeting-text {
    font-size: 20px !important;
  }

  .chart-container { height: 200px; }
  .chart-container-bar { height: 220px; }
}

@media (max-width: 480px) {
  .todo-grid { grid-template-columns: 1fr; }
  .finance-overview { grid-template-columns: 1fr; }
  
  .header-right {
    .action-btn span {
      display: none; /* 小屏幕刷新按钮只保留图标 */
    }
  }

  .greeting {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 8px !important;
  }

  .module-header {
    flex-direction: column;
    align-items: flex-start !important;
  }
}
</style>